import { getSigner } from './getContract';

/**
 * Подпись под изменением данных на бэке.
 *
 * Бэк восстанавливает по подписи адрес и пускает только админа. Текст обязан
 * совпадать с `buildMessage` в ../salary-back (`services/authService.ts`) до
 * символа — иначе 401/403. Эталонные строки проверяют тесты на обеих сторонах.
 */

export interface SignedAction {
  title: string;
  fields: Array<[string, string]>;
}

export const actions = {
  addEmployee: (
    fullName: string,
    wallet: string,
    salary: string,
  ): SignedAction => ({
    title: 'добавить сотрудника',
    fields: [
      ['ФИО', fullName],
      ['Кошелёк', wallet.toLowerCase()],
      ['Оклад', salary],
    ],
  }),
  updateSalary: (wallet: string, salary: string): SignedAction => ({
    title: 'изменить оклад',
    fields: [
      ['Кошелёк', wallet.toLowerCase()],
      ['Оклад', salary],
    ],
  }),
  updateBonus: (wallet: string, bonus: string): SignedAction => ({
    title: 'изменить премию',
    fields: [
      ['Кошелёк', wallet.toLowerCase()],
      ['Премия', bonus],
    ],
  }),
  updatePenalty: (wallet: string, penalty: string): SignedAction => ({
    title: 'изменить штраф',
    fields: [
      ['Кошелёк', wallet.toLowerCase()],
      ['Штраф', penalty],
    ],
  }),
  deleteEmployee: (wallet: string): SignedAction => ({
    title: 'удалить сотрудника',
    fields: [['Кошелёк', wallet.toLowerCase()]],
  }),
  // Без хэша транзакции: подпись берётся до отправки выплаты в контракт.
  recordPayout: (amount: string, date: string): SignedAction => ({
    title: 'записать выплату',
    fields: [
      ['Сумма', amount],
      ['Дата', date],
    ],
  }),
};

export function buildMessage(action: SignedAction, issuedAt: string): string {
  return [
    `EMIVN Salary: ${action.title}`,
    ...action.fields.map(([label, value]) => `${label}: ${value}`),
    `Время: ${issuedAt}`,
  ].join('\n');
}

export type SignatureHeaders = Record<'X-Signature' | 'X-Signed-At', string>;

/**
 * Подписывает действие кошельком и отдаёт заголовки для запроса. Вызывается
 * ДО транзакции в контракт: откажись оператор от подписи после — контракт и
 * база разошлись бы.
 */
export async function signAction(
  action: SignedAction,
): Promise<SignatureHeaders> {
  const issuedAt = new Date().toISOString();
  const signer = await getSigner();

  let signature: string;

  try {
    signature = await signer.signMessage(buildMessage(action, issuedAt));
  } catch (error) {
    console.error('signMessage:', error);
    throw new Error('Подпись отклонена в кошельке — ничего не изменено');
  }

  return { 'X-Signature': signature, 'X-Signed-At': issuedAt };
}
